import { t } from './sprache'

/**
 * Welche Art Dimension eine Zelle ohne Wert trägt.
 *
 * - `gesellschaft` und `konto` haben **keinen Vorgabewert**: Jede Buchung
 *   trägt ein Konto, jede gelieferte und jede gerechnete Zeile gehört einer
 *   Gesellschaft. Fehlt die Angabe, ist das eine Lücke.
 * - `weitere` sind die Dimensionen **mit** Vorgabewert — Partner,
 *   Bewegungsart, Segment, Kostenstelle, die Analysedimensionen des Kunden.
 *   Dort ist die leere Wahl eine Ausprägung: Eine Buchung ohne Partner ist eine
 *   Buchung gegen Extern.
 * - `keine` ist alles, was keine Dimension mit Vorgabewert ist — Kurs,
 *   Währung, Bewertungsebene, Periode. Leer heißt dort: keine Angabe.
 */
export type Dimensionsart = 'gesellschaft' | 'konto' | 'weitere' | 'keine'

/**
 * **Was eine Dimension ohne Wert zeigt — eine Stelle für die ganze
 * Oberfläche** (7.3 Abschnitt 5.1, R429, R1650).
 *
 * Bei einer Dimension mit Vorgabewert dieser: „Default". Bei Gesellschaft,
 * Konto und allem, was keine solche Dimension ist, der Gedankenstrich wie in
 * einer Summenzeile.
 *
 * **Der Befund vom 2026-10-12:** Im Wertenachweis von 2100 Kapitalrücklage der
 * Tochter USA in Euro stand die Zeile *Währungsdifferenz* mit „Default" bei
 * Gesellschaft und Konto — „da es diese Elementbezeichnung in den Dimensionen
 * Gesellschaft und Konto nicht gibt (aus gutem Grund)". Dieselbe Ersatzanzeige
 * stand im Wertenachweis der Kapitalflussrechnung, im Buchungsjournal und in
 * den Vorgängen. `Ohnewert.test.ts` lässt „Default" nur über diese Stelle zu.
 */
export function ohneWert(dimension: Dimensionsart): string {
  return dimension === 'weitere' ? t('dimension.default') : '—'
}

/**
 * Die Spalten des Wertenachweises, deren Dimension einen Vorgabewert hat —
 * **ausdrücklich aufgezählt** (R1650): Bewegungsart (`was`), Partner, Segment
 * und die Analysedimensionen (`dim1` … `dim8`).
 *
 * **Der Befund vom 2026-10-12:** Seit die umgerechneten Zeilen ihren Kurs
 * tragen (R1652), erscheint die Spalte *Kurs* — und an der Zeile
 * *Währungsdifferenz* stand dort „Default". Bis dahin bekam jede Spalte, die
 * nicht Gesellschaft oder Konto hieß, den Vorgabewert; jetzt bekommt ihn nur,
 * wer hier steht.
 */
const MIT_VORGABEWERT = new Set(['was', 'partner', 'segment'])

/** Die Art der Dimension nach dem Schlüssel einer Spalte des Wertenachweises. */
export function dimensionsart(spalte: string): Dimensionsart {
  if (spalte === 'einheit') return 'gesellschaft'
  if (spalte === 'konto') return 'konto'
  return MIT_VORGABEWERT.has(spalte) || /^dim\d+$/.test(spalte) ? 'weitere' : 'keine'
}
